import { ChangeDetectionStrategy, Component, computed, inject, input, output } from "@angular/core";
import { AppConfig } from "@api/models/app-config";
import { AccessToken } from "@auth/models/token";
import { AboutArgs } from "@layout/components/about/about-args";
import { AboutComponent } from "@layout/components/about/about.component";
import { dividerPtOptions } from "@layout/components/user-menu/divider-pt";
import { menuPtOptions } from "@layout/components/user-menu/menu-pt";
import { ICenter } from "@shared/models/center";
import { MenuItem } from "primeng/api";
import { ButtonPassThrough, Button } from "primeng/button";
import { DialogService } from "primeng/dynamicdialog";
import { Avatar } from "primeng/avatar";
import { Menu } from "primeng/menu";
import { Divider } from "primeng/divider";

export interface UserMenuPermissionsArgs {
    canAccessACC: boolean;
    canAccessACU: boolean;
    isDRVSEnabled: boolean;
    isACWAdmin: boolean;
    isMultiCenter: boolean;
}

@Component({
    selector: "app-user-menu",
    templateUrl: "./user-menu.component.html",
    styleUrl: "./user-menu.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [Button, Avatar, Menu, Divider],
})
export class UserMenuComponent {
    public menuPt = menuPtOptions;
    public dividerPt = dividerPtOptions;

    public userMenuPermissionArgs = input.required<UserMenuPermissionsArgs>();
    public appConfig = input.required<AppConfig>();
    public token = input<AccessToken | null>(null);
    public scopedCenter = input<ICenter | null>(null);
    public pt = input<ButtonPassThrough | null>(null);
    public logoutClicked = output<null>();
    public changeCenterClicked = output<null>();
    public settingsClicked = output<null>();

    private readonly dialogService = inject(DialogService);

    public avatarLabel = computed(() => {
        const token = this.token();
        if (!token?.userFirstName || !token.userLastName) return "N/A";
        return `${token.userFirstName[0]}${token.userLastName[0]}`;
    });

    public userName = computed(() => {
        const token = this.token();
        if (!token?.userFirstName || !token.userLastName) return "N/A";
        return `${token.userFirstName} ${token.userLastName}`;
    });

    public userEmail = computed(() => {
        const token = this.token();
        if (!token) return "N/A";
        return token.userEmail;
    });

    public menuOptions = computed(() => [
        {
            label: "YOUR APPS",
            items: this.assembleApplicationMenuItems(),
        },
        {
            label: "ADMINISTRATION",
            items: this.assembleAdminMenuItems(),
        },
        {
            label: "ACCOUNT",
            items: [
                {
                    label: "Modify Profile",
                    command: () => window.open(this.appConfig().myAzaraAccountUrl, "_blank"),
                    data: {
                        styleClass: "user-menu__modify-profile",
                    },
                },
            ],
        },
        {
            label: "OTHER",
            items: [
                {
                    label: "About",
                    command: () => {
                        const currentUser =
                            this.token()?.userFirstName && this.token()?.userLastName
                                ? `${this.token()?.userFirstName} ${this.token()?.userLastName}`
                                : "N/A";
                        const scopedCenter =
                            this.scopedCenter()?.name && this.scopedCenter()?.id
                                ? `${this.scopedCenter()?.name} (${this.scopedCenter()?.id})`
                                : "N/A";

                        this.dialogService.open(AboutComponent, {
                            header: "About",
                            width: "75vw",
                            modal: true,
                            closable: true,
                            dismissableMask: true,
                            closeOnEscape: true,
                            data: {
                                token: this.token(),
                                claimsToDisplay: [
                                    "userEmail",
                                    "permission",
                                    "viewPhi",
                                    "planId",
                                    "tenant",
                                    "centerId",
                                    "identityProvider",
                                ],
                                customInformation: {
                                    "Scoped Center": scopedCenter,
                                    "Current User": currentUser,
                                },
                            } as AboutArgs,
                        });
                    },
                    icon: "assets/layout/images/azara-info.svg",
                    data: {
                        styleClass: "user-menu__about",
                    },
                },
            ],
        },
    ]);

    handleSwitchCenters(): void {
        this.changeCenterClicked.emit(null);
    }

    handleLogout() {
        this.logoutClicked.emit(null);
    }

    private openSettingsTab(): void {
        this.settingsClicked.emit(null);
    }

    private assembleApplicationMenuItems(): MenuItem[] {
        const accessibleApplications: MenuItem[] = [];
        const { isDRVSEnabled, canAccessACC, canAccessACU } = this.userMenuPermissionArgs();

        if (isDRVSEnabled) {
            accessibleApplications.push({
                label: "DRVS",
                command: () => window.open(this.appConfig().drvsUrl, "_blank"),
                data: {
                    styleClass: "user-menu__drvs",
                },
            });
        }

        if (canAccessACC) {
            accessibleApplications.push({
                label: "Care Connect",
                command: () => window.open(this.appConfig().accUrl, "_blank"),
                data: {
                    styleClass: "user-menu__acc",
                },
            });
        }

        if (canAccessACU) {
            accessibleApplications.push({
                label: "Cost & Utilization",
                command: () => window.open(this.appConfig().acuUrl, "_blank"),
                data: {
                    styleClass: "user-menu__costutil",
                },
            });
        }

        return accessibleApplications;
    }

    private assembleAdminMenuItems(): MenuItem[] {
        const accessibleAdminMenuItems: MenuItem[] = [];

        if (this.userMenuPermissionArgs().isMultiCenter) {
            accessibleAdminMenuItems.push({
                label: "Change Center",
                command: () => this.handleSwitchCenters(),
                data: {
                    styleClass: "user-menu__change-center",
                },
            });
        }

        accessibleAdminMenuItems.push({
            label: "Settings",
            command: () => this.openSettingsTab(),
            data: {
                styleClass: "user-menu__settings",
            },
        });

        return accessibleAdminMenuItems;
    }
}
